import { Button, Select, Space, Input } from 'antd';
import { cloneDeep } from 'lodash';

import { ChangeEvent, useState } from 'react';
import { companyApproval, companyInternalApproval } from '../../../api';
import { messageError } from '../../../components/message';
import { ModalWrap } from '../../../components/modal-wrap';
import {
  CompanyRejectTypeLabelMap,
  InternalCertRejectTypeLabelMap,
} from '../../../config/company';
import getFormatMessage from '../../../locales/get-format-message';
import { ICompanyRejectType, ICompanyRejectReason } from '../../../model';

import {
  CommonStatusStep,
  IAPICompanyFullInfo,
  StudioCertifiedType,
} from '../../../model/api-modules';
import styles from './index.module.scss';

interface IProps {
  companyInfo: IAPICompanyFullInfo;
  onOk: () => void;
  onCancel: () => void;
}

const getTypeTextOptions = (
  isInternal: boolean,
  rejectReasons: readonly ICompanyRejectReason[],
  currentKey?: ICompanyRejectType
) => {
  let map: any = CompanyRejectTypeLabelMap;
  if (isInternal) {
    map = InternalCertRejectTypeLabelMap;
  }
  return Object.keys(map)
    .filter(
      (typeKey) =>
        typeKey === currentKey ||
        rejectReasons.every((reason) => reason.type !== typeKey)
    )
    .map((typeKey) => {
      return {
        label: map[typeKey as ICompanyRejectType],
        value: typeKey,
      };
    })
    .filter((item) => item.label);
};

export const RejectModal = (props: IProps) => {
  const { companyInfo, onOk, onCancel } = props;

  const [rejectReasons, setRejectReasons] = useState<
    ReadonlyArray<ICompanyRejectReason>
  >([{ id: new Date().getTime(), type: undefined, reason: '' }]);

  const addReason = () => {
    setRejectReasons([
      ...rejectReasons,
      { id: new Date().getTime(), type: undefined, reason: '' },
    ]);
  };
  const removeReason = (id?: number) => {
    setRejectReasons([...rejectReasons].filter((item) => item.id !== id));
  };
  const setType = (
    updatedReason: ICompanyRejectReason,
    value: ICompanyRejectType
  ) => {
    const allReasons = cloneDeep(rejectReasons);
    const matchedReason = allReasons.find(
      (reason) => reason.id === updatedReason.id
    );
    if (matchedReason) {
      matchedReason.type = value;
    }
    setRejectReasons(allReasons);
  };

  const setReasonText = (
    updatedReason: ICompanyRejectReason,
    value: ChangeEvent<HTMLInputElement>
  ) => {
    const allReasons = cloneDeep(rejectReasons);
    const matchedReason = allReasons.find(
      (reason) => reason.id === updatedReason.id
    );
    if (matchedReason) {
      matchedReason.reason = value.target.value;
    }
    setRejectReasons(allReasons);
  };

  return (
    <ModalWrap
      width="650px"
      title="审核拒绝确认"
      oKText={getFormatMessage('submit')}
      cancelText={getFormatMessage('cancel')}
      onOk={async () => {
        const reasons = rejectReasons
          .filter((item) => item.type && item.reason)
          .map((item) => {
            return {
              type: item.type,
              reason: item.reason,
            };
          });
        if (!reasons.length) {
          messageError('请输入原因');
          return false;
        }

        const params = {
          status: CommonStatusStep.Reject,
          companyType: 0,
          rejectReason: JSON.stringify(reasons),
        };
        let res = false;
        if (companyInfo.certifiedType === StudioCertifiedType.Company) {
          res = await companyApproval(companyInfo.StudioID, params);
        } else {
          res = await companyInternalApproval(companyInfo.StudioID, params);
        }

        if (res) {
          onOk();
          return true;
        }
        messageError('操作失败');
        return false;
      }}
      onClose={onCancel}
    >
      <div className={styles.modalContent}>
        <Space direction="vertical">
          {rejectReasons.map((rejectReason, index) => {
            const rejectOptions = getTypeTextOptions(
              !!companyInfo.InternalCertInfo,
              rejectReasons,
              rejectReason.type
            );
            return (
              <Space.Compact block size="large">
                <Select
                  placeholder="请选择类型"
                  style={{ width: 150 }}
                  options={rejectOptions}
                  value={rejectReason.type}
                  onChange={(value) => setType(rejectReason, value)}
                />
                <Input
                  placeholder="请输入原因"
                  value={rejectReason.reason}
                  style={{ width: 350 }}
                  onChange={(value) => setReasonText(rejectReason, value)}
                />
                {index === rejectReasons.length - 1 &&
                  rejectOptions.length > 1 && (
                    <Button shape="circle" onClick={addReason}>
                      +
                    </Button>
                  )}

                {rejectReasons.length > 1 && (
                  <Button
                    shape="circle"
                    onClick={removeReason.bind(null, rejectReason.id)}
                  >
                    -
                  </Button>
                )}
              </Space.Compact>
            );
          })}
        </Space>
      </div>
    </ModalWrap>
  );
};
